import { Fragment, useState } from "react";
import { formatCurrency } from "../../../utils/formatters";
import AccessGate from "../../../components/AccessGate";
import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";
import { useAuth } from "../../../context/AuthContexts";
import {
  useGetPaymentEditRequestsQuery,
  useApprovePaymentRequestMutation,
  useRejectPaymentRequestMutation,
  useGetPaymentAuditTrailQuery,
  useGetLedgerAccountsQuery,
} from "../api/billingApi";

type Tab = "pending" | "approved" | "rejected";

const fmtDateTime = (v?: string | null) =>
  v ? new Date(v).toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" }) : "—";

/**
 * Did the signed-in admin raise this request?
 *
 * Approving your own amendment is permitted but not silent: the backend demands
 * a reason and stamps the audit trail as self-approved. This drives both the
 * warning on the row and the required-reason state in the approve dialog.
 * Ids are compared loosely because one side arrives as a string over JSON.
 */
function isOwnRequest(req: any, currentUser: any): boolean {
  const requester = req?.requested_by?.id;
  const me = currentUser?.id;
  return requester != null && me != null && String(requester) === String(me);
}

/** One labelled value in the detail panel. */
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">{label}</div>
      <div className="text-[11px] font-semibold text-slate-800 break-words">{value ?? "—"}</div>
    </div>
  );
}

/** Human label for an audit-trail entry type. */
const TRAIL_LABEL: Record<string, string> = {
  created: "Payment recorded",
  edit_requested: "Edit requested",
  edit_approved: "Edit approved & applied",
  edit_rejected: "Edit rejected",
  delete_requested: "Delete requested",
  delete_approved: "Payment deleted",
  delete_rejected: "Delete rejected",
  reverified: "Re-verified",
};

/** Render a before/after (or snapshot) block from an audit entry. */
function ChangeBlock({ changes }: { changes: any }) {
  if (!changes || typeof changes !== "object") return null;

  const rows: Array<[string, any, any]> = [];
  if (changes.from || changes.to) {
    const keys = Array.from(
      new Set([...Object.keys(changes.from ?? {}), ...Object.keys(changes.to ?? {})]),
    ).filter((k) => k !== "allocations");
    keys.forEach((k) => {
      const a = changes.from?.[k];
      const b = changes.to?.[k];
      if (JSON.stringify(a) !== JSON.stringify(b)) rows.push([k, a, b]);
    });
  }

  const deleted = changes.deleted;

  return (
    <div className="mt-2 space-y-2">
      {/* Approved by the same admin who raised it — the reason is in the notes
          above. Flagged so an auditor scanning the trail cannot miss it. */}
      {changes.self_approved && (
        <div className="inline-flex items-center gap-1 rounded-lg bg-amber-50 border border-amber-200 px-2 py-1">
          <span className="text-[9px] font-black uppercase tracking-widest text-amber-800">
            Self-approved — no second admin
          </span>
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded-lg bg-white border border-slate-100 p-2 space-y-1">
          {rows.map(([k, a, b]) => (
            <div key={k} className="flex flex-wrap items-center gap-1 text-[10px]">
              <span className="font-black uppercase tracking-widest text-slate-400">{k}</span>
              <span className="line-through text-rose-400">{String(a ?? "—")}</span>
              <span className="text-slate-300">→</span>
              <span className="font-bold text-emerald-600">{String(b ?? "—")}</span>
            </div>
          ))}
        </div>
      )}

      {deleted && (
        <div className="rounded-lg bg-white border border-rose-100 p-2">
          <div className="text-[9px] font-black uppercase tracking-widest text-rose-500 mb-1">
            Deleted payment snapshot
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Fact label="Amount" value={deleted.amount != null ? formatCurrency(deleted.amount) : "—"} />
            <Fact label="Receipt" value={deleted.receipt_number} />
            <Fact label="Reference" value={<span className="font-mono">{deleted.reference}</span>} />
            <Fact label="Method" value={deleted.payment_method} />
            <Fact label="Paid at" value={deleted.paid_at} />
            <Fact label="Gateway" value={deleted.gateway} />
          </div>
          {Array.isArray(deleted.allocations) && deleted.allocations.length > 0 && (
            <div className="mt-2">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">
                It had settled
              </div>
              {deleted.allocations.map((a: any, i: number) => (
                <div key={i} className="flex justify-between text-[10px] text-slate-600">
                  <span>{a.description ?? "Item"}{a.year ? ` (${a.year})` : ""}</span>
                  <span className="font-bold">{formatCurrency(a.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Expanded detail + full audit log for one request. */
function RequestDetail({ req }: { req: any }) {
  const { data, isFetching } = useGetPaymentAuditTrailQuery(req.payment_id);
  const trail = data?.trail ?? [];
  const paymentGone = data?.payment_exists === false;

  const p = req.payment;
  const snap = req.deleted_snapshot;

  return (
    <div className="p-5 bg-slate-50 border-t border-slate-100 space-y-5">
      {paymentGone && (
        <div className="rounded-xl bg-rose-50 border border-rose-100 px-3 py-2 text-[11px] font-bold text-rose-700">
          This payment was deleted. The details below come from the audit snapshot.
        </div>
      )}

      {/* Who / what */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Fact label="Member" value={p?.user?.name ?? "—"} />
        <Fact label="Membership No" value={p?.user?.member?.membership_no} />
        <Fact label="Email" value={p?.user?.email} />
        <Fact label="Requested by" value={req.requested_by?.name} />
        <Fact
          label="Amount"
          value={(p?.amount ?? snap?.amount) != null ? formatCurrency(p?.amount ?? snap?.amount) : "—"}
        />
        <Fact label="Receipt No" value={p?.receipt_number ?? snap?.receipt_number} />
        <Fact
          label="Reference"
          value={<span className="font-mono">{p?.reference ?? snap?.reference ?? "—"}</span>}
        />
        <Fact label="Method" value={p?.payment_method ?? snap?.payment_method} />
        <Fact label="Paid at" value={p?.paid_at ? String(p.paid_at).slice(0, 10) : snap?.paid_at} />
        <Fact label="Gateway" value={p?.gateway ?? snap?.gateway} />
        <Fact label="Bank" value={p?.bank?.name} />
        <Fact label="Status" value={p?.status ?? snap?.status} />
      </div>

      {/* Invoice + what it settled */}
      {(p?.invoice || (p?.allocations?.length ?? 0) > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {p?.invoice && (
            <div className="rounded-xl bg-white border border-slate-100 p-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">Invoice</div>
              <div className="grid grid-cols-2 gap-2">
                <Fact label="Number" value={p.invoice.invoice_number} />
                <Fact label="Status" value={p.invoice.status} />
                <Fact label="Total" value={formatCurrency(p.invoice.total_amount)} />
                <Fact label="Balance" value={formatCurrency(p.invoice.balance)} />
              </div>
            </div>
          )}

          {(p?.allocations?.length ?? 0) > 0 && (
            <div className="rounded-xl bg-white border border-slate-100 p-3">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">
                What this payment settled
              </div>
              <div className="space-y-1">
                {p.allocations.map((a: any) => (
                  <div key={a.id} className="flex justify-between text-[11px] text-slate-700">
                    <span>
                      {a.invoice_item?.description ?? "Item"}
                      {a.invoice_item?.year ? ` (${a.invoice_item.year})` : ""}
                    </span>
                    <span className="font-bold">{formatCurrency(a.amount)}</span>
                  </div>
                ))}
              </div>
              {req.type === "delete" && (
                <p className="mt-2 text-[10px] text-rose-600 font-semibold">
                  Approving will reverse these allocations and restore the balances.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Reason / notes in full */}
      <div className="rounded-xl bg-white border border-slate-100 p-3 space-y-2">
        <Fact label="Reason for request" value={<span className="italic">{req.reason || "—"}</span>} />
        {req.notes && <Fact label="Requester notes" value={req.notes} />}
        {req.response_reason && <Fact label="Response" value={req.response_reason} />}
      </div>

      {/* Audit log */}
      <div>
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
          Audit log
        </div>
        {isFetching ? (
          <div className="h-16 bg-slate-100 rounded-xl animate-pulse" />
        ) : trail.length === 0 ? (
          <p className="text-[11px] text-slate-400 italic">No audit entries recorded for this payment.</p>
        ) : (
          <ol className="space-y-2">
            {trail.map((t: any, i: number) => (
              <li key={i} className="rounded-xl bg-white border border-slate-100 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[11px] font-black text-slate-800">
                    {TRAIL_LABEL[t.type] ?? t.type}
                  </span>
                  <span className="text-[10px] text-slate-400 font-semibold">{fmtDateTime(t.created_at)}</span>
                </div>
                <div className="mt-1 text-[10px] text-slate-500">
                  By <span className="font-bold text-slate-700">{t.actor?.name ?? "—"}</span>
                  {t.responder && (
                    <> · Actioned by <span className="font-bold text-slate-700">{t.responder.name}</span></>
                  )}
                </div>
                {t.reason && <p className="mt-1 text-[10px] text-slate-600 italic">{t.reason}</p>}
                {t.notes && <p className="text-[10px] text-slate-400">Note: {t.notes}</p>}
                <ChangeBlock changes={t.changes} />
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

const tabStyle = (active: boolean) =>
  active
    ? "px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl bg-slate-900 text-white"
    : "px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl text-slate-500 hover:bg-slate-100 transition-all";

function RequestTypeChip({ type }: { type: string }) {
  return type === "delete" ? (
    <span className="inline-flex px-2 py-0.5 rounded-lg border text-[9px] font-black uppercase tracking-widest bg-rose-50 text-rose-700 border-rose-100">
      Delete
    </span>
  ) : (
    <span className="inline-flex px-2 py-0.5 rounded-lg border text-[9px] font-black uppercase tracking-widest bg-orange-50 text-orange-700 border-orange-100">
      Edit
    </span>
  );
}

function RejectDialog({
  onConfirm,
  onCancel,
  isLoading,
}: {
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-sm p-6 space-y-4">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Reject Request</h3>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for rejection..."
          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none focus:ring-2 ring-rose-100 resize-none"
        />
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            disabled={!reason.trim() || isLoading}
            onClick={() => onConfirm(reason.trim())}
            className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {isLoading ? "Rejecting..." : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ApproveNotesDialog({
  onConfirm,
  onCancel,
  isLoading,
  requestType,
  selfApproval = false,
}: {
  onConfirm: (notes: string) => void;
  onCancel: () => void;
  isLoading: boolean;
  requestType: string;
  /** Approver is also the requester — the backend then demands a reason. */
  selfApproval?: boolean;
}) {
  const [notes, setNotes] = useState("");
  // Mirrors the server rule, so the admin is told before submitting rather
  // than bounced back with a 422.
  const notesMissing = selfApproval && notes.trim() === "";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-sm p-6 space-y-4">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">
          Confirm {requestType === "delete" ? "Deletion" : "Edit"}
        </h3>
        {requestType === "delete" && (
          <div className="flex items-start gap-2 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2">
            <svg className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-[10px] font-bold text-rose-700">
              This will permanently delete the payment. This cannot be undone.
            </p>
          </div>
        )}
        {selfApproval && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
            <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-[10px] font-bold text-amber-800">
              You raised this request yourself. Amendments are normally approved by a
              second admin — state why you are approving your own. This is recorded
              against the payment.
            </p>
          </div>
        )}
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={selfApproval ? "Reason for approving your own request (required)…" : "Optional approval notes..."}
          className={`w-full border rounded-xl px-3 py-2 text-sm bg-slate-50 outline-none focus:ring-2 ${
            notesMissing ? "border-amber-300 ring-amber-100" : "border-slate-200 ring-emerald-100"
          }`}
        />
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            disabled={isLoading || notesMissing}
            onClick={() => onConfirm(notes.trim())}
            className={`flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl text-white disabled:opacity-50 ${
              requestType === "delete" ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            {isLoading ? "Processing..." : "Confirm Approve"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PaymentEditRequestsInner() {
  // Amending a settled payment is a two-admin control: whoever raised the
  // request cannot approve it. Knowing who we are lets the row say so up front
  // instead of letting the admin click through to a 403.
  const { user: currentUser } = useAuth();

  const [tab, setTab] = useState<Tab>("pending");
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  // Dialogs
  const [rejectTarget, setRejectTarget] = useState<any>(null);
  const [approveTarget, setApproveTarget] = useState<any>(null);
  const [expandedId, setExpandedId] = useState<number | string | null>(null);

  const { data, isLoading, refetch } = useGetPaymentEditRequestsQuery({
    status: tab,
    page,
    per_page: 25,
  });

  // A proposed bank account arrives as an id; the approver has to see which
  // account that is before agreeing to move the ledger credit onto it.
  const { data: bankAccounts = [] } = useGetLedgerAccountsQuery();
  const accountName = (id: any) => {
    const a = bankAccounts.find((acc: any) => String(acc.id) === String(id));
    return a ? a.account_name || a.name || `Account #${id}` : `Account #${id}`;
  };

  const [approveRequest, { isLoading: approving }] = useApprovePaymentRequestMutation();
  const [rejectRequest, { isLoading: rejecting }] = useRejectPaymentRequestMutation();

  const requests: any[] = data?.data ?? data?.requests?.data ?? [];
  const meta = data?.requests ?? data ?? {};

  const showToast = (type: "success" | "error", msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const handleApprove = async (notes: string) => {
    if (!approveTarget) return;
    try {
      await approveRequest({ id: approveTarget.id, body: { notes } }).unwrap();
      showToast("success", `Request ${approveTarget.type === "delete" ? "deletion" : "edit"} approved successfully.`);
      setApproveTarget(null);
      refetch();
    } catch (err: any) {
      showToast("error", err?.data?.message || "Approval failed.");
      setApproveTarget(null);
    }
  };

  const handleReject = async (reason: string) => {
    if (!rejectTarget) return;
    try {
      await rejectRequest({ id: rejectTarget.id, body: { reason } }).unwrap();
      showToast("success", "Request rejected.");
      setRejectTarget(null);
      refetch();
    } catch (err: any) {
      showToast("error", err?.data?.message || "Rejection failed.");
      setRejectTarget(null);
    }
  };

  return (
    <div className="p-6 space-y-6 bg-[#F8FAFC] min-h-screen">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black text-slate-900 uppercase tracking-widest">
            Payment Edit / Delete Requests
          </h1>
          <p className="text-xs text-slate-400 font-bold mt-1">
            Review and approve or reject payment modification requests raised by admins.
          </p>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 px-4 py-3 rounded-2xl shadow-xl text-sm font-bold text-white ${toast.type === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.msg}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 bg-white p-2 rounded-2xl border border-slate-200 w-fit shadow-sm">
        {(["pending", "approved", "rejected"] as Tab[]).map((t) => (
          <button key={t} onClick={() => { setTab(t); setPage(1); }} className={tabStyle(tab === t)}>
            {t}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : requests.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-slate-400 font-black uppercase tracking-widest text-sm">
              No {tab} requests
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="px-2 py-3 w-8" />
                  {["Type", "Payment", "Member", "Requested By", "Reason", "Proposed Changes", "Date", ...(tab === "pending" ? ["Actions"] : ["Responded By", "Response"])].map((h) => (
                    <th key={h} className="px-4 py-3 text-[9px] font-black uppercase tracking-widest text-slate-400">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.map((req: any) => (
                  <Fragment key={req.id}>
                  <tr className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    {/* Expand */}
                    <td className="px-2 py-3">
                      <button
                        onClick={() => setExpandedId(expandedId === req.id ? null : req.id)}
                        aria-label={expandedId === req.id ? "Hide details" : "Show details"}
                        title={expandedId === req.id ? "Hide details" : "Show full details & audit log"}
                        className="w-6 h-6 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-all"
                      >
                        <span className={`transition-transform inline-block ${expandedId === req.id ? "rotate-90" : ""}`}>
                          ›
                        </span>
                      </button>
                    </td>

                    {/* Type */}
                    <td className="px-4 py-3">
                      <RequestTypeChip type={req.type} />
                    </td>

                    {/* Payment */}
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="font-mono font-black text-slate-700 text-[10px]">
                          {req.payment?.receipt_number || req.payment?.reference || `#${req.payment_id}`}
                        </span>
                        <span className="text-slate-400 text-[9px] font-bold">
                          {req.payment?.amount != null ? formatCurrency(req.payment.amount) : "—"}
                        </span>
                      </div>
                    </td>

                    {/* Member */}
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-800">{req.payment?.user?.name || "—"}</span>
                        <span className="text-[9px] text-slate-400 font-black uppercase tracking-widest">
                          {req.payment?.user?.member?.membership_no || ""}
                        </span>
                      </div>
                    </td>

                    {/* Requested by */}
                    <td className="px-4 py-3">
                      <span className="font-semibold text-slate-700">{req.requested_by?.name || "—"}</span>
                    </td>

                    {/* Reason — shown in full; wraps rather than truncating */}
                    <td className="px-4 py-3 align-top min-w-[220px] max-w-[320px]">
                      <p className="text-slate-600 italic whitespace-pre-wrap break-words leading-relaxed">
                        {req.reason || "—"}
                      </p>
                      {req.notes && (
                        <p className="mt-1 text-slate-400 text-[9px] whitespace-pre-wrap break-words leading-relaxed">
                          Note: {req.notes}
                        </p>
                      )}
                    </td>

                    {/* Proposed changes */}
                    <td className="px-4 py-3">
                      {req.type === "delete" ? (
                        <span className="text-rose-500 font-black text-[9px] uppercase tracking-widest">Payment will be removed</span>
                      ) : req.changes ? (
                        <div className="space-y-0.5">
                          {req.changes.amount != null && (
                            <div className="text-[9px]">
                              <span className="text-slate-400">Amt:</span>{" "}
                              <span className="line-through text-rose-400">{formatCurrency(req.payment?.amount)}</span>{" → "}
                              <span className="text-emerald-600 font-bold">{formatCurrency(req.changes.amount)}</span>
                            </div>
                          )}
                          {req.changes.payment_method && (
                            <div className="text-[9px]">
                              <span className="text-slate-400">Method:</span>{" "}
                              <span className="line-through text-rose-400">{req.payment?.payment_method}</span>{" → "}
                              <span className="text-emerald-600 font-bold">{req.changes.payment_method}</span>
                            </div>
                          )}
                          {req.changes.paid_at && (
                            <div className="text-[9px]">
                              <span className="text-slate-400">Date:</span>{" "}
                              <span className="line-through text-rose-400">{req.payment?.paid_at?.slice(0, 10)}</span>{" → "}
                              <span className="text-emerald-600 font-bold">{req.changes.paid_at}</span>
                            </div>
                          )}
                          {req.changes.reference && (
                            <div className="text-[9px]">
                              <span className="text-slate-400">Ref:</span>{" "}
                              <span className="line-through text-rose-400 font-mono">{req.payment?.reference}</span>{" → "}
                              <span className="text-emerald-600 font-bold font-mono">{req.changes.reference}</span>
                            </div>
                          )}
                          {req.changes.bank_account_id != null && (
                            <div className="text-[9px]">
                              <span className="text-slate-400">Bank:</span>{" "}
                              <span className="line-through text-rose-400">{req.payment?.bank?.name ?? "—"}</span>{" → "}
                              <span className="text-emerald-600 font-bold">{accountName(req.changes.bank_account_id)}</span>
                              <span className="block text-amber-600 font-bold">Moves the ledger credit</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {req.created_at
                        ? new Date(req.created_at).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" })
                        : "—"}
                    </td>

                    {tab === "pending" ? (
                      /* Actions — deciding an amendment is its own permission,
                         separate from being able to see the queue. */
                      <td className="px-4 py-3">
                        <AccessGate
                          perm={PERMS.billing.amendmentApprove}
                          fallback={
                            <span className="text-[9px] font-black uppercase tracking-widest text-slate-300">
                              Awaiting approver
                            </span>
                          }
                        >
                          <div className="flex flex-col gap-1.5">
                            <div className="flex gap-2">
                              <button
                                onClick={() => setApproveTarget(req)}
                                className="px-3 py-1.5 bg-emerald-600 text-white text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-emerald-700 transition-all"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => setRejectTarget(req)}
                                className="px-3 py-1.5 bg-rose-50 text-rose-700 border border-rose-100 text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-rose-100 transition-all"
                              >
                                Reject
                              </button>
                            </div>
                            {/* Allowed, but not silent — the reason is compulsory
                                and the trail records it as self-approved. */}
                            {isOwnRequest(req, currentUser) && (
                              <span className="text-[9px] font-black uppercase tracking-widest text-amber-600">
                                Your own request — reason required
                              </span>
                            )}
                          </div>
                        </AccessGate>
                      </td>
                    ) : (
                      <>
                        {/* Responded by */}
                        <td className="px-4 py-3">
                          <span className="font-semibold text-slate-700">{req.responded_by?.name || "—"}</span>
                        </td>
                        {/* Response */}
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex px-2 py-0.5 rounded-lg border text-[9px] font-black uppercase tracking-widest ${
                              tab === "approved"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                : "bg-rose-50 text-rose-700 border-rose-100"
                            }`}>
                              {tab}
                            </span>
                            {req.response_reason && (
                              <span className="text-[9px] text-slate-400 italic">{req.response_reason}</span>
                            )}
                            {req.responded_at && (
                              <span className="text-[9px] text-slate-400">
                                {new Date(req.responded_at).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" })}
                              </span>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>

                  {expandedId === req.id && (
                    <tr>
                      <td colSpan={tab === "pending" ? 9 : 10} className="p-0">
                        <RequestDetail req={req} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && meta.last_page > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-all"
            >
              Previous
            </button>
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Page {page} of {meta.last_page}
            </span>
            <button
              disabled={page >= meta.last_page}
              onClick={() => setPage((p) => p + 1)}
              className="px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-all"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Approve dialog */}
      {approveTarget && (
        <ApproveNotesDialog
          requestType={approveTarget.type}
          isLoading={approving}
          selfApproval={isOwnRequest(approveTarget, currentUser)}
          onConfirm={handleApprove}
          onCancel={() => setApproveTarget(null)}
        />
      )}

      {/* Reject dialog */}
      {rejectTarget && (
        <RejectDialog
          isLoading={rejecting}
          onConfirm={handleReject}
          onCancel={() => setRejectTarget(null)}
        />
      )}
    </div>
  );
}

/**
 * Seeing the amendment queue is its own permission — it exposes payment
 * history and the reasons admins gave for changing settled records. Deciding
 * an amendment is gated separately, inside.
 */
export default function PaymentEditRequestsPage() {
  return (
    <PageGate perm={PERMS.billing.amendmentAccess}>
      <PaymentEditRequestsInner />
    </PageGate>
  );
}
